import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, CreditCard, CheckCircle, FileText, ChevronDown, ChevronUp, MessageCircle, Calculator } from 'lucide-react';
import axios from 'axios';
import { serviceSchema, breadcrumbSchema } from '../utils/schemaOrg';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, [slug, currentLanguage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [serviceRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/services/${slug}?language=${currentLanguage}`),
        axios.get(`${API_URL}/api/service-categories`)
      ]);
      setService(serviceRes.data);
      setCategories(catRes.data);
      setError(null);
    } catch (err) {
      console.error('Error loading service:', err);
      setError('Услуга не найдена');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryKey) => {
    const cat = categories.find(c => c.key === categoryKey);
    if (cat) {
      return currentLanguage === 'RU' ? cat.name_ru : cat.name_en;
    }
    return categoryKey;
  };

  const toggleFaq = (index) => {
    setExpandedFaq(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const openModal = (title) => {
    setModalTitle(title);
    setIsModalOpen(true);
  };

  // Parse markdown-style text with **bold** formatting and [links](url)
  const parseTextWithFormatting = (text) => {
    if (!text) return null;
    
    // First handle links [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    const parts = [];
    let match;
    let keyIdx = 0;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        parts.push(...parseBoldText(beforeText, keyIdx));
        keyIdx += 10;
      }
      
      const linkText = match[1];
      const url = match[2];
      
      // Check if internal link
      if (url.startsWith('/')) {
        parts.push(
          <Link key={`link-${keyIdx++}`} to={url} className="text-orange-600 hover:text-orange-700 underline">
            {linkText}
          </Link>
        );
      } else {
        parts.push(
          <a key={`link-${keyIdx++}`} href={url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">
            {linkText}
          </a>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(...parseBoldText(text.substring(lastIndex), keyIdx));
    }
    
    return parts.length > 0 ? parts : text;
  };
  
  // Helper to parse **bold** text
  const parseBoldText = (text, startKey) => {
    const boldParts = text.split(/(\*\*[^*]+\*\*)/g);
    return boldParts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`bold-${startKey}-${index}`}>{part.slice(2, -2)}</strong>;
      }
      return part;
    }).filter(p => p !== '');
  };

  // Parse content blocks with table support
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let inList = false;
    let tableRows = [];
    let inTable = false;
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Check for table row
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        // Flush any pending list
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`list-${index}`} className="space-y-2 mb-4">
              {currentList.map((item, i) => (
                <li key={i} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{parseTextWithFormatting(item)}</span>
                </li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
        
        // Skip separator row (|---|---|)
        if (trimmedLine.includes('---')) {
          return;
        }
        
        inTable = true;
        const cells = trimmedLine.split('|').filter(cell => cell.trim() !== '');
        tableRows.push(cells.map(c => c.trim()));
        return;
      }
      
      // If we were in a table and now we're not, render the table
      if (inTable && !trimmedLine.startsWith('|')) {
        if (tableRows.length > 0) {
          const headerRow = tableRows[0];
          const dataRows = tableRows.slice(1);
          
          elements.push(
            <div key={`table-${index}`} className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    {headerRow.map((cell, ci) => (
                      <th key={ci} className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-4 py-3 text-sm text-gray-600 border-b">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          tableRows = [];
        }
        inTable = false;
      }
      
      if (trimmedLine.startsWith('• ') || trimmedLine.startsWith('* ')) {
        inList = true;
        currentList.push(trimmedLine.substring(2));
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        // Heading
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`list-${index}`} className="space-y-2 mb-4">
              {currentList.map((item, i) => (
                <li key={i} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{parseTextWithFormatting(item)}</span>
                </li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
        elements.push(
          <h3 key={`h-${index}`} className="text-lg font-semibold text-gray-900 mt-6 mb-3">
            {trimmedLine.slice(2, -2)}
          </h3>
        );
      } else if (trimmedLine && !inTable) {
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`list-${index}`} className="space-y-2 mb-4">
              {currentList.map((item, i) => (
                <li key={i} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{parseTextWithFormatting(item)}</span>
                </li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
        elements.push(
          <p key={`p-${index}`} className="text-gray-600 mb-4 leading-relaxed">
            {parseTextWithFormatting(trimmedLine)}
          </p>
        );
      }
    });
    
    // Handle remaining table
    if (inTable && tableRows.length > 0) {
      const headerRow = tableRows[0];
      const dataRows = tableRows.slice(1);
      
      elements.push(
        <div key="table-final" className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                {headerRow.map((cell, ci) => (
                  <th key={ci} className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-sm text-gray-600 border-b">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    
    // Handle remaining list items
    if (currentList.length > 0) {
      elements.push(
        <ul key="list-final" className="space-y-2 mb-4">
          {currentList.map((item, i) => (
            <li key={i} className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600">{parseTextWithFormatting(item)}</span>
            </li>
          ))}
        </ul>
      );
    }
    
    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'RU' ? 'Услуга не найдена' : 'Service not found'}
          </h1>
          <Button onClick={() => navigate('/services')} className="bg-orange-500 hover:bg-orange-600">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {currentLanguage === 'RU' ? 'К списку услуг' : 'Back to services'}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const faqItems = service.faq || [];
  const workComplex = service.work_complex || [];
  const requiredDocs = service.required_docs || [];
  const fullContent = service.full_content;
  
  // SEO meta tags
  const seoTitle = service.seo_title || `${service.title} | ВЕЛОКС ХИНТ`;
  const seoDescription = service.seo_description || service.description;
  const seoKeywords = service.seo_keywords || '';
  const pageUrl = `${window.location.origin}/services/${service.slug}`;
  const ogLocale = currentLanguage === 'EN' ? 'en_US' : 'ru_RU';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <html lang={currentLanguage === 'EN' ? 'en' : 'ru'} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {seoKeywords && <meta name="keywords" content={seoKeywords} />}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content={currentLanguage === 'EN' ? 'ru_RU' : 'en_US'} />
        <link rel="canonical" href={pageUrl} />
        {/* hreflang language alternates — same URL serves both languages via in-app toggle */}
        <link rel="alternate" hrefLang="ru" href={pageUrl} />
        <link rel="alternate" hrefLang="en" href={pageUrl} />
        <link rel="alternate" hrefLang="x-default" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify([
            serviceSchema({ service, language: currentLanguage }),
            breadcrumbSchema([
              { name: currentLanguage === 'EN' ? 'Home' : 'Главная', path: '/' },
              { name: currentLanguage === 'EN' ? 'Services' : 'Услуги', path: '/services' },
              { name: service.title, path: `/services/${service.slug}` },
            ]),
          ])}
        </script>
      </Helmet>
      
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <button onClick={() => navigate('/')} className="text-gray-500 hover:text-orange-500">
              {currentLanguage === 'RU' ? 'Главная' : 'Home'}
            </button>
            <span className="text-gray-400">/</span>
            <button onClick={() => navigate('/services')} className="text-gray-500 hover:text-orange-500">
              {currentLanguage === 'RU' ? 'Услуги' : 'Services'}
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{service.title}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="inline-block px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-medium mb-4">
                {getCategoryName(service.category)}
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {service.title}
              </h1>
              <p className="text-lg text-gray-600">
                {service.description}
              </p>
            </div>

            {/* Full Content */}
            {fullContent && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="prose prose-gray max-w-none">
                  {renderContent(fullContent)}
                </div>
              </div>
            )}

            {/* Work Complex Section */}
            {workComplex && workComplex.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {currentLanguage === 'RU' ? 'Комплекс выполняемых работ ВЕЛОКС ХИНТ' : 'VELOX HINT Work Complex'}
                </h2>
                <div className="space-y-3">
                  {workComplex.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-orange-600 font-semibold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-gray-600 pt-1">{parseTextWithFormatting(item)}</p>
                    </div>
                  ))}
                </div>
                
                {/* Required Documents */}
		{requiredDocs && requiredDocs.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {currentLanguage === 'RU' ? 'Что требуется от вас для старта:' : 'What you need to start:'}
                    </h3>
                    <ul className="space-y-2">
                      {requiredDocs.map((doc, index) => {
                        // Быстрый встроенный словарь для на лету перевода базовых документов ВЭД
                        let displayDoc = doc;
                        if (currentLanguage === 'EN') {
                          const dict = {
                            "Контракт": "Contract", "контракт": "contract",
                            "Инвойс": "Invoice", "инвойс": "invoice",
                            "Устав": "Articles of Association", "устав": "charter",
                            "Спецификация": "Specification", "спецификация": "specification",
                            "Заявка": "Application form", "заявка": "application",
                            "Доверенность": "Power of Attorney", "доверенность": "power of attorney",
                            "Техническое описание": "Technical description", "техническое описание": "technical description",
                            "Паспорт безопасности": "MSDS / Safety passport",
                            "ОГРН": "OGRN (State Registration Certificate)",
                            "ИНН": "INN (Tax Registration Certificate)"
                          };
                          // Ищем совпадения ключевых слов
                          for (const [key, value] of Object.entries(dict)) {
                            if (doc.includes(key)) {
                              displayDoc = doc.replace(key, value);
                            }
                          }
                          // Если перевод не найден, оставляем оригинал или транслит
                          if (displayDoc === doc) {
                            displayDoc = doc; 
                          }
                        }
                        return (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{displayDoc}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* CTA Button after Work Complex */}
                <div className="mt-6 pt-6 border-t">
                  <Button 
                    onClick={() => openModal(currentLanguage === 'RU' ? 'Бесплатный анализ' : 'Free Analysis')}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 text-lg"
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    {currentLanguage === 'RU' ? 'Обращайся, не стесняйся, анализ бесплатный' : 'Contact us, analysis is free'}
                  </Button>
                </div>
              </div>
            )}

            {/* FAQ Section */}
            {faqItems && faqItems.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {currentLanguage === 'RU' ? 'Часто задаваемые вопросы' : 'FAQ'}
                </h2>
                <div className="space-y-3">
                  {faqItems.map((faq, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition"
                      >
                        <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                        {expandedFaq[index] ? (
                          <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                        )}
                      </button>
                      {expandedFaq[index] && (
                        <div className="px-4 pb-4 text-gray-600 border-t bg-gray-50">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Button to FAQ page */}
                <div className="mt-6 pt-6 border-t">
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/faq')}
                    className="w-full border-orange-500 text-orange-600 hover:bg-orange-50 py-3"
                  >
                    {currentLanguage === 'RU' ? 'Ещё больше ответов' : 'More answers'}
                  </Button>
                </div>
              </div>
            )}

            {/* CTA Button - Получить расчет (separate block) */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <Button 
                onClick={() => openModal(currentLanguage === 'RU' ? 'Получить расчет' : 'Get Estimate')}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-4 text-lg"
              >
                <Calculator className="mr-2 h-5 w-5" />
                {currentLanguage === 'RU' ? 'Получить расчет' : 'Get Estimate'}
              </Button>
            </div>

            {/* Legacy Detailed Description (for old services) */}
            {!fullContent && service.detailed_description && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {currentLanguage === 'RU' ? 'Подробное описание' : 'Detailed Description'}
                </h2>
                
                {service.detailed_description.full_text && (
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.detailed_description.full_text}
                  </p>
                )}

                {service.detailed_description.includes && service.detailed_description.includes.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {currentLanguage === 'RU' ? 'Что включено:' : 'What is included:'}
                    </h3>
                    <ul className="space-y-2">
                      {service.detailed_description.includes.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              {/* Price & Timeframe Card */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-4">
                  {currentLanguage === 'RU' ? 'Условия' : 'Terms'}
                </h3>
                
                <div className="space-y-4">
                  {/* Timeframe */}
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">
                        {currentLanguage === 'RU' ? 'Срок выполнения' : 'Timeframe'}
                      </div>
                      <div className="font-semibold text-gray-900">
                        {service.timeframe}
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  {service.price && (
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <CreditCard className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">
                          {currentLanguage === 'RU' ? 'Стоимость' : 'Price'}
                        </div>
                        <div className="font-semibold text-gray-900">
                          {service.price}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 mt-4">
                  <Button 
                    onClick={() => openModal(currentLanguage === 'RU' ? 'Заказать услугу' : 'Order Service')}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3"
                  >
                    {currentLanguage === 'RU' ? 'Заказать услугу' : 'Order Service'}
                  </Button>
                </div>
              </div>

	      {/* Document Preview Image */}
	      {service.preview_image && (
                <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-6">
                  <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                    <FileText className="mr-2 h-4 w-4 text-orange-500" />
                    {currentLanguage === 'RU' ? 'Образец документа' : 'Document Sample'}
                  </h2>
                
                  {/* Превью-картинка в боковой панели */}
                  <div 
                    onClick={() => window.showVeloxModal && window.showVeloxModal(service.preview_image)}
                    className="relative rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex justify-center cursor-zoom-in group"
                  >
                    <img
                      src={service.preview_image}
                      alt={service.title}
                      className="w-full h-auto object-contain max-h-[250px] transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-center text-[11px] text-gray-400 mt-2">
                    {currentLanguage === 'RU' ? 'Нажмите на изображение для увеличения' : 'Click to enlarge'}
                  </p>

                  {/* Скрипт-инициализатор модального окна (встраивается один раз) */}
                  <VeloxLightbox />
                </div>
              )}

              {/* Contact Card */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-sm p-6 text-white">
                <h3 className="font-bold mb-2">
                  {currentLanguage === 'RU' ? 'Нужна консультация?' : 'Need consultation?'}
                </h3>
                <p className="text-orange-100 text-sm mb-4">
                  {currentLanguage === 'RU' 
                    ? 'Свяжитесь с нами, и мы ответим на все ваши вопросы'
                    : 'Contact us and we will answer all your questions'}
                </p>
                <Button 
                  variant="secondary"
                  onClick={() => openModal(currentLanguage === 'RU' ? 'Получить консультацию' : 'Get Consultation')}
                  className="w-full bg-white text-orange-600 hover:bg-orange-50"
                >
                  {currentLanguage === 'RU' ? 'Получить консультацию' : 'Get Consultation'}
                </Button>
              </div>

              {/* Back Button */}
              <button 
                onClick={() => navigate('/services')}
                className="flex items-center text-gray-600 hover:text-orange-500 transition"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {currentLanguage === 'RU' ? 'Вернуться к списку услуг' : 'Back to services'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
      />
    </div>
  );
};

// Компонент умного модального окна для увеличения документов
function VeloxLightbox() {
  const [imgUrl, setImgUrl] = React.useState(null);
  React.useEffect(() => {
    window.showVeloxModal = (url) => setImgUrl(url);
    return () => { window.showVeloxModal = null; };
  }, []);
  if (!imgUrl) return null;
  return (
    <div 
      onClick={() => setImgUrl(null)} 
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out animate-fadeIn"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div className="relative max-w-4xl max-h-[90vh] bg-transparent rounded-xl overflow-hidden flex items-center justify-center">
        <img 
          src={imgUrl} 
          alt="Enlarged Document" 
          className="max-w-full max-h-[85vh] object-contain shadow-2xl rounded-lg border border-white/10"
        />
        <button 
          onClick={() => setImgUrl(null)}
          className="absolute top-4 right-4 bg-black/50 text-white hover:bg-black/80 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-colors"
        >
          ×
        </button>
      </div>
    </div>
  );
}
export default ServiceDetail;
